import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AssignVehiclesRequest,
  BPOContextSchema,
  CompanionCoveragesReviewResponse,
  CompleteCompanionCoveragesRequest,
  CompleteCompanionCoveragesResponse,
  CoverageCategory,
  CoverageListItem,
  CoverageListResponse,
  CoverageMentionSchema,
  CoverageSchema,
  CreateCoverageSchema,
  LorAutofillReviewPayload,
  LorReviewWorkflowStatusResponse,
  MergeCoverageRequest,
  OpenClaimContextSchema,
  StartLorReviewResponse,
  UpdateBPOContextRequest,
  UpdateCoverageSchema,
  UpdateOpenClaimContextRequest,
} from '../types/coverage-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import type { CorrespondencePackageSchema } from './correspondenceService';

// Coverage API
export const coverageApiClient = createApi({
  reducerPath: 'coverageApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Coverages', 'Coverage', 'BPOContext', 'CompanionCoverages'] as const,
  endpoints: (builder) => ({
    getCoverages: builder.query<
      CoverageListItem[],
      { matterId: string; category?: CoverageCategory; includeShared?: boolean }
    >({
      query: ({ matterId, category, includeShared }) => {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (includeShared) params.append('include_shared', 'true');
        const queryString = params.toString();
        return queryString
          ? `api/matters/${matterId}/coverages/?${queryString}`
          : `api/matters/${matterId}/coverages/`;
      },
      transformResponse: (response: CoverageListResponse) => response.results,
      providesTags: (result, error, { matterId }) => [
        { type: 'Coverages', id: matterId },
      ],
    }),
    // Coverage contact handles (coverage 1-to-many contact handles)
    getCoverageContactsCount: builder.query<
      number,
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) =>
        `api/matters/${matterId}/coverages/${coverageId}/contact-handles/`,
      transformResponse: (response: unknown[]) =>
        Array.isArray(response) ? response.length : 0,
    }),
    addCoverageContact: builder.mutation<
      { status: 'ok' },
      { matterId: string; coverageId: string; contactHandleId: number }
    >({
      query: ({ matterId, coverageId, contactHandleId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/contact-handles/`,
        method: 'POST',
        body: { handle_id: contactHandleId },
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),
    removeCoverageContact: builder.mutation<
      void,
      { matterId: string; coverageId: string; contactHandleId: number }
    >({
      query: ({ matterId, coverageId, contactHandleId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/contact-handles/${contactHandleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),
    getCoverage: builder.query<
      CoverageSchema,
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),
    submitLorAutofillReview: builder.mutation<
      { status: 'ok' },
      {
        matterId: string;
        coverageId: string;
        body: LorAutofillReviewPayload;
      }
    >({
      query: ({ matterId, coverageId, body }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/lor-autofill-review/`,
        method: 'POST',
        body,
        cache: 'no-store',
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || response.statusText);
          }
          const reader = response.body?.getReader();
          if (!reader) {
            return { status: 'ok' };
          }
          try {
            // Consume the server-sent events stream so the request can complete.
            // Stream reading must be sequential, so await in loop is necessary
            // eslint-disable-next-line no-constant-condition
            while (true) {
              // eslint-disable-next-line no-await-in-loop
              const { done } = await reader.read();
              if (done) break;
            }
          } finally {
            reader.releaseLock();
          }
          return { status: 'ok' };
        },
      }),
    }),
    // Async LOR review - starts a workflow and returns immediately
    submitLorAutofillReviewAsync: builder.mutation<
      StartLorReviewResponse,
      {
        matterId: string;
        coverageId: string;
        body: LorAutofillReviewPayload;
      }
    >({
      query: ({ matterId, coverageId, body }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/lor-autofill-review-async/`,
        method: 'POST',
        body,
      }),
    }),
    // Get status of async LOR review workflow
    getLorReviewWorkflowStatus: builder.query<
      LorReviewWorkflowStatusResponse,
      {
        matterId: string;
        coverageId: string;
        workflowId: string;
      }
    >({
      query: ({ matterId, coverageId, workflowId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/lor-autofill-review-async/status/${workflowId}/`,
        method: 'GET',
      }),
    }),
    // Async workflow-based upload that returns immediately
    uploadLorFileAsync: builder.mutation<
      {
        workflow_id: string;
        blob_id: string;
        artifact_id: string;
        coverage_id: string;
        document_instance_id: string;
      },
      {
        matterId: string;
        coverageId: string;
        file: File;
        threadId?: string;
        templateId?: string | null;
      }
    >({
      query: ({ matterId, coverageId, file, threadId, templateId }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (threadId) {
          formData.append('thread_id', threadId);
        }
        if (templateId) {
          formData.append('template_id', templateId);
        }
        return {
          url: `api/matters/${matterId}/coverages/${coverageId}/upload-lor-async/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),
    // Query workflow status for progress polling
    getUploadLorWorkflowStatus: builder.query<
      {
        workflow_id: string;
        status: string;
        status_description: string;
        is_running: boolean;
        started_at: string | null;
        completed_at: string | null;
        error_message: string | null;
        coverage_id: string | null;
        blob_id: string | null;
        artifact_id: string | null;
      },
      { matterId: string; coverageId: string; workflowId: string }
    >({
      query: ({ matterId, coverageId, workflowId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/upload-lor-async/status/${workflowId}/`,
        method: 'GET',
      }),
    }),
    submitUploadedLorReview: builder.mutation<
      { status: 'ok' },
      {
        matterId: string;
        coverageId: string;
        artifactId?: string;
        threadId?: string;
      }
    >({
      query: ({ matterId, coverageId, artifactId, threadId }) => {
        const body: Record<string, string> = {};
        if (artifactId) {
          body.artifact_id = artifactId;
        }
        if (threadId) {
          body.thread_id = threadId;
        }
        return {
          url: `api/matters/${matterId}/coverages/${coverageId}/upload-lor/review/`,
          method: 'POST',
          body: Object.keys(body).length ? body : undefined,
          cache: 'no-store',
          responseHandler: async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(errorText || response.statusText);
            }
            const reader = response.body?.getReader();
            if (!reader) {
              return { status: 'ok' };
            }
            try {
              // Stream reading must be sequential, so await in loop is necessary
              // eslint-disable-next-line no-constant-condition
              while (true) {
                // eslint-disable-next-line no-await-in-loop
                const { done } = await reader.read();
                if (done) break;
              }
            } finally {
              reader.releaseLock();
            }
            return { status: 'ok' };
          },
        };
      },
    }),
    getUploadedLorMarkdown: builder.query<
      {
        markdown: string;
        length: number;
        is_truncated: boolean;
        artifact_id: string;
      },
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/upload-lor/markdown/`,
        method: 'GET',
      }),
    }),
    createCoverage: builder.mutation<
      CoverageSchema,
      { matterId: string; coverage: Partial<CreateCoverageSchema> }
    >({
      query: ({ matterId, coverage }) => ({
        url: `api/matters/${matterId}/coverages/`,
        method: 'POST',
        body: { ...coverage, matter_id: matterId },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Coverages', id: matterId },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Lazy import to avoid circular dependency (tasksService imports coverageApiClient)
          const { tasksApiClient } = await import('./tasksService');
          dispatch(
            tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']),
          );
        } catch {
          /* no-op */
        }
      },
    }),
    updateCoverage: builder.mutation<
      CoverageSchema,
      {
        matterId: string;
        coverageId: string;
        coverage: Partial<UpdateCoverageSchema>;
      }
    >({
      query: ({ matterId, coverageId, coverage }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/`,
        method: 'PATCH',
        body: { ...coverage, id: coverageId },
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverages', id: matterId },
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),
    lorDataUpdated: builder.mutation<
      { status: 'ok' },
      {
        matterId: string;
        coverageId: string;
        body: {
          document_id?: string | null;
          mapping?: string | null;
          old_value?: string | null;
          new_value?: string | null;
        };
      }
    >({
      query: ({ matterId, coverageId, body }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/lor_data_updated/`,
        method: 'POST',
        body,
      }),
    }),
    deleteCoverage: builder.mutation<
      void,
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Coverages', id: matterId },
      ],
    }),
    mergeCoverages: builder.mutation<
      CoverageSchema,
      { matterId: string; mergeCoverageRequest: MergeCoverageRequest }
    >({
      query: ({ matterId, mergeCoverageRequest }) => ({
        url: `api/matters/${matterId}/coverages/merge_coverages/`,
        method: 'POST',
        body: mergeCoverageRequest,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Coverages', id: matterId },
      ],
    }),
    regenerateCoverage: builder.mutation<
      void,
      { matterId: string; artifactIds: string[] }
    >({
      query: ({ matterId, artifactIds }) => ({
        url: `api/matters/${matterId}/mentioned-coverages/regenerate_mentions/`,
        method: 'POST',
        body: { artifact_ids: artifactIds },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Coverages', id: matterId },
      ],
    }),
    addLorToDocuments: builder.mutation<
      {
        status: 'created' | 'already_exists';
        blob_id: string;
        collection_id?: string;
        message: string;
      },
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/add-lor-to-documents/`,
        method: 'POST',
      }),
    }),
    preparePremisesCertifiedMail: builder.mutation<
      CorrespondencePackageSchema,
      {
        matterId: string;
        coverageId: string;
        taskId: string;
        createNewAttempt: boolean;
        lorDocumentId?: string;
        contactId: string;
        contactIndex?: number;
      }
    >({
      query: ({
        matterId,
        coverageId,
        taskId,
        createNewAttempt,
        lorDocumentId,
        contactId,
        contactIndex,
      }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/prepare-premises-certified-mail/`,
        method: 'POST',
        body: {
          task_id: taskId,
          create_new_attempt: createNewAttempt,
          ...(lorDocumentId ? { lor_document_id: lorDocumentId } : {}),
          contact_id: contactId,
          ...(contactIndex !== undefined ? { contact_index: contactIndex } : {}),
        },
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),
    /**
     * Promote user's LOR merge field selections to position 0 (primary/default).
     *
     * This endpoint should be called when the user finalizes their LOR selections.
     * It ensures the chosen values become the primary values on the coverage without
     * creating duplicates. If a value already exists (case-insensitive), it's moved
     * to position 0; otherwise, a new entry is created at position 0.
     */
    promoteLorSelections: builder.mutation<
      {
        coverage_id: string;
        claims_updated: boolean;
        policy_identifiers_updated: boolean;
        adjusters_updated: boolean;
      },
      {
        matterId: string;
        coverageId: string;
        claimNumber?: string;
        claimBenefitType?: string;
        policyNumber?: string;
        policyIdentifierType?: string;
        adjusterName?: string;
        adjusterEmail?: string;
        adjusterPhone?: string;
        adjusterFax?: string;
        adjusterBenefitType?: string;
      }
    >({
      query: ({
        matterId,
        coverageId,
        claimNumber,
        claimBenefitType,
        policyNumber,
        policyIdentifierType,
        adjusterName,
        adjusterEmail,
        adjusterPhone,
        adjusterFax,
        adjusterBenefitType,
      }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/promote-lor-selections/`,
        method: 'POST',
        body: {
          claim_number: claimNumber,
          claim_benefit_type: claimBenefitType,
          policy_number: policyNumber,
          policy_identifier_type: policyIdentifierType,
          adjuster_name: adjusterName,
          adjuster_email: adjusterEmail,
          adjuster_phone: adjusterPhone,
          adjuster_fax: adjusterFax,
          adjuster_benefit_type: adjusterBenefitType,
        },
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
        { type: 'Coverages', id: matterId },
      ],
    }),

    // BPO Context endpoints
    getBpoContext: builder.query<BPOContextSchema, { matterId: string }>({
      query: ({ matterId }) => `api/matters/${matterId}/coverages/bpo-context/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'BPOContext', id: matterId },
      ],
    }),

    assignVehiclesToCoverage: builder.mutation<
      CoverageSchema,
      { matterId: string; coverageId: string; vehicleIds: string[] }
    >({
      query: ({ matterId, coverageId, vehicleIds }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/assign-vehicles/`,
        method: 'PATCH',
        body: { vehicle_ids: vehicleIds } as AssignVehiclesRequest,
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
        { type: 'Coverages', id: matterId },
        { type: 'BPOContext', id: matterId },
      ],
    }),

    updateBpoContext: builder.mutation<
      BPOContextSchema,
      { matterId: string; updates: UpdateBPOContextRequest }
    >({
      query: ({ matterId, updates }) => ({
        url: `api/matters/${matterId}/coverages/bpo-context/`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (result, error, { matterId, updates }) => [
        { type: 'BPOContext', id: matterId },
        // Also invalidate Coverage since open_claim_context gets cleared on save
        { type: 'Coverages', id: matterId },
        ...(updates.coverage_id
          ? [
              {
                type: 'Coverage' as const,
                id: `${matterId}-${updates.coverage_id}`,
              },
            ]
          : []),
      ],
    }),

    // Open Claim Context endpoints

    /**
     * Get the open claim context for a specific coverage.
     * This contains AI-discovered data with citations for opening a claim.
     */
    getOpenClaimContext: builder.query<
      OpenClaimContextSchema | null,
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) =>
        `api/matters/${matterId}/coverages/${coverageId}/open-claim-context/`,
      providesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
      ],
    }),

    /**
     * Start the Open Claim auto-discovery workflow.
     * Triggers AI-powered document search to find and verify claim information.
     */
    runOpenClaimDiscovery: builder.mutation<
      { workflow_id: string; coverage_id: string; thread_id: string | null },
      {
        matterId: string;
        coverageId: string;
        threadId?: string;
        taskId?: string;
      }
    >({
      query: ({ matterId, coverageId, threadId, taskId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/run-discovery/`,
        method: 'POST',
        body: { thread_id: threadId, task_id: taskId },
      }),
      // Invalidate coverage to refetch open_claim_context after discovery completes
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
        { type: 'Coverages', id: matterId },
      ],
    }),

    /**
     * Update the open claim context for a coverage.
     * Used when the user edits and saves fields from the Open Claim Review modal.
     */
    updateOpenClaimContext: builder.mutation<
      CoverageSchema,
      {
        matterId: string;
        coverageId: string;
        updates: UpdateOpenClaimContextRequest;
      }
    >({
      query: ({ matterId, coverageId, updates }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/open-claim-context/`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (result, error, { matterId, coverageId }) => [
        { type: 'Coverage', id: `${matterId}-${coverageId}` },
        { type: 'Coverages', id: matterId },
      ],
    }),

    /**
     * Get coverage activity (domain events) for a specific coverage.
     */
    getCoverageActivity: builder.query<
      { event: Record<string, unknown> }[],
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/activity/`,
        method: 'GET',
      }),
    }),

    // Companion coverage sharing endpoints
    getCompanionCoveragesReview: builder.query<
      CompanionCoveragesReviewResponse,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/companion-coverages/review/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'CompanionCoverages', id: matterId },
      ],
    }),

    completeCompanionCoverages: builder.mutation<
      CompleteCompanionCoveragesResponse,
      { matterId: string; body: CompleteCompanionCoveragesRequest }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/companion-coverages/complete/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { matterId, body }) => {
        const companionMatterIds = [
          ...new Set(body.associations.map((a) => a.target_matter_id)),
        ];
        return [
          { type: 'Coverages', id: matterId },
          { type: 'CompanionCoverages', id: matterId },
          ...companionMatterIds.flatMap((id) => [
            { type: 'Coverages' as const, id },
            { type: 'CompanionCoverages' as const, id },
          ]),
        ];
      },
    }),
    createOpenFileTask: builder.mutation<
      {
        created_tasks: string[];
        updated_tasks: string[];
        skipped_tasks: string[];
      },
      { matterId: string; coverageId: string }
    >({
      query: ({ matterId, coverageId }) => ({
        url: `api/matters/${matterId}/coverages/${coverageId}/create-open-file/`,
        method: 'POST',
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Invalidate task data so the new task appears in the tasks panel
          const { tasksApiClient } = await import('./tasksService');
          dispatch(
            tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']),
          );
        } catch {
          /* no-op */
        }
      },
    }),
  }),
});

// Mentioned Coverages API (was "Coverage Mentions API")
export const mentionedCoveragesApiClient = createApi({
  reducerPath: 'mentionedCoveragesApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['MentionedCoverages', 'MentionedCoverage'] as const,
  endpoints: (builder) => ({
    getMentionedCoverages: builder.query<
      CoverageMentionSchema[],
      {
        matterId: string;
        artifactId?: string;
        coverageId?: string;
        mentionIds?: string[];
      }
    >({
      query: ({ matterId, artifactId, coverageId, mentionIds }) => {
        const url = `api/matters/${matterId}/mentioned-coverages/`;
        const params = new URLSearchParams();

        if (artifactId) params.append('artifact_id', artifactId);
        if (coverageId) params.append('coverage_id', coverageId);
        if (mentionIds?.length)
          params.append('mention_ids', mentionIds.join(','));

        const queryString = params.toString();
        return queryString ? `${url}?${queryString}` : url;
      },
      providesTags: (result, error, { matterId }) => [
        { type: 'MentionedCoverages', id: matterId },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetCoveragesQuery,
  useGetCoverageQuery,
  useCreateCoverageMutation,
  useSubmitLorAutofillReviewMutation,
  useSubmitLorAutofillReviewAsyncMutation,
  useGetLorReviewWorkflowStatusQuery,
  useLazyGetLorReviewWorkflowStatusQuery,
  useUploadLorFileAsyncMutation,
  useGetUploadLorWorkflowStatusQuery,
  useLazyGetUploadLorWorkflowStatusQuery,
  useSubmitUploadedLorReviewMutation,
  useGetUploadedLorMarkdownQuery,
  useUpdateCoverageMutation,
  useDeleteCoverageMutation,
  useMergeCoveragesMutation,
  useRegenerateCoverageMutation,
  useLorDataUpdatedMutation,
  useGetCoverageContactsCountQuery,
  useAddCoverageContactMutation,
  useRemoveCoverageContactMutation,
  useAddLorToDocumentsMutation,
  usePreparePremisesCertifiedMailMutation,
  usePromoteLorSelectionsMutation,
  // BPO Context hooks
  useGetBpoContextQuery,
  useAssignVehiclesToCoverageMutation,
  useUpdateBpoContextMutation,
  // Open Claim Context hooks
  useGetOpenClaimContextQuery,
  useRunOpenClaimDiscoveryMutation,
  useUpdateOpenClaimContextMutation,
  useGetCoverageActivityQuery,
  // Companion coverage sharing hooks
  useGetCompanionCoveragesReviewQuery,
  useCompleteCompanionCoveragesMutation,
  // Open File task creation hook
  useCreateOpenFileTaskMutation,
} = coverageApiClient;

export const { useGetMentionedCoveragesQuery } = mentionedCoveragesApiClient;
